import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users, MessageSquare, BookOpen, Target, Star, Brain,
  Trophy, Shield, Bell, ArrowRight, ChevronDown, ChevronUp,
  CheckCircle, BarChart3, Eye, Send, AlertTriangle, Zap,
  Heart, FileText, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SiteNav, SiteFooter } from './HomePage';

const SECTIONS = [
  {
    id: 'dashboard', title: 'Your Staff Dashboard', icon: BarChart3, color: '#E86C3D',
    description: 'The moment you log in, everything you need is right here.',
    content: 'Your dashboard shows 5 KPI cards at the top (My Youth, Check-ins, Courses Done, Needs Attention, Goals Done), an Activity Feed with high-signal events, and tabs for My Youth, Courses, Messages, Journals, and Moderation.',
    features: ['5 KPI cards at a glance', 'Activity Feed showing course completions, goals, badges, wins', '"Message All" button for quick announcements', 'AI Insights analyzing your youth group']
  },
  {
    id: 'youth-cards', title: 'Youth Cards — Know Every Person', icon: Users, color: '#3B82F6',
    description: 'Each youth in your group has an identity-focused card with everything you need.',
    content: 'Cards show: name, avatar, interests (blue tags), school/worksite, career goals, points, workshop progress, and risk flags. Red border = needs immediate attention. Amber = watch. Filter by: All, Needs Attention, No Courses, Stalled.',
    features: ['Interests and career goals visible at a glance', 'Risk flags: inactive 7+ days, stalled courses, no goals', '"Nudge This Group" button to message filtered youth', 'Click any card → opens their full Development Timeline']
  },
  {
    id: 'timeline', title: 'Youth Development Timeline', icon: Eye, color: '#8B5CF6',
    description: 'Click any youth card to see their complete journey — every milestone, chronologically.',
    content: 'The timeline shows: when they joined, goals set and completed, check-ins, courses started and finished, badges earned, wins posted. Workshop summary cards show Done, In Progress, XP, and Badges.',
    features: ['Chronological view of all milestones', 'Color-coded dots: green=completed, orange=in-progress, red=stalled', 'Workshop summary at top', '"Generate AI Profile Report" button for instant analysis']
  },
  {
    id: 'ai-reports', title: 'AI Profile Reports', icon: Brain, color: '#06B6D4',
    description: 'One click generates a comprehensive AI analysis of any youth — before every meeting.',
    content: 'The AI pulls from ALL platform data: interests, skills, goals, courses, workbook reflections, assessment scores, wins, check-ins. It generates: Summary, Strengths, Growth Areas, and Recommendations.',
    features: ['Uses GPT-4o analyzing 43+ data points per youth', 'Strengths identified from actual platform activity', 'Growth areas based on incomplete courses and goals', 'Specific recommendations for how to support them']
  },
  {
    id: 'messaging', title: 'Messaging & Communication', icon: MessageSquare, color: '#10B981',
    description: 'Direct messaging with youth, parent communication, Q&A, and must-read announcements.',
    content: 'The Messages tab combines: Youth Q&A questions (with "Add to FAQ" button), parent messages, and group messages. "Must Read" messages block the youth platform until acknowledged.',
    features: ['Youth ask questions → you answer → promote to FAQ', 'Parent messages with email notifications both ways', '"Message All" sends to entire group', '"Must Read" toggle forces youth to acknowledge before using platform']
  },
  {
    id: 'courses', title: 'Workshop Analytics', icon: BookOpen, color: '#F59E0B',
    description: 'See exactly which courses your youth are completing, stalled on, or haven\'t started.',
    content: 'The Courses tab shows: Total Completions, Courses Started, Youth Engaged, Stalled count. Per-course breakdown with completion bars. Per-youth workshop status grid.',
    features: ['13 interactive courses tracked per youth', 'Completion bars per course', 'Click any youth name to open their timeline', 'Identify who needs a nudge to finish stalled courses']
  },
  {
    id: 'assessment', title: 'Assessment Tracking', icon: Target, color: '#EF4444',
    description: 'Track who completed their assessments, their scores, and growth over time.',
    content: 'View completion tracker for your cohort, individual scores and bands (Emerging → Advanced), and supportive flags for youth scoring very low.',
    features: ['Pre/post assessment comparison', 'Per-domain score breakdown (6 competency areas)', 'Supportive flags for at-risk youth', 'Data feeds into AI reports']
  },
  {
    id: 'daily', title: 'Your Daily Workflow', icon: Calendar, color: '#8B5CF6',
    description: 'What to do every day, every week, and before every meeting.',
    content: 'Morning: Check Activity Feed → respond to messages → review risk flags. Weekly: Review Courses tab → nudge stalled youth → generate AI reports. Before meetings: Click youth card → view timeline → generate AI report → walk in prepared.',
    features: ['Morning routine: 5 minutes to see everything', 'Weekly: identify who needs support', 'Before meetings: AI report gives you the full picture', 'All data auto-collected — you never enter anything manually']
  },
];

const StaffTutorialPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) setActiveSection(entry.target.id); });
    }, { threshold: 0.3 });
    SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SiteNav />

      {/* Sticky section nav */}
      <div className="sticky top-[57px] z-40 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-2 py-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0 transition-all ${activeSection === s.id ? 'bg-orange-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                <s.icon className="w-3.5 h-3.5" />
                {s.title.split(' — ')[0].split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 50%, #2D9EA3 100%)' }} className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Staff Guide:<br /><span className="text-orange-400">Your Command Center</span>
          </h1>
          <p className="text-lg text-white/70 max-w-lg mx-auto">Everything your lead monitors, mentors, and counselors need to know — in one interactive guide.</p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-16">
        {SECTIONS.map((section, i) => (
          <section key={section.id} id={section.id}>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${section.color}15` }}>
                  <section.icon className="w-6 h-6" style={{ color: section.color }} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">{section.title}</h2>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed mb-5">{section.content}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {section.features.map((f, fi) => (
                  <motion.div key={fi} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: fi * 0.1 }}
                    className="flex items-start gap-2 p-3 rounded-xl bg-gray-50">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: section.color }} />
                    <p className="text-sm text-gray-700">{f}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>
        ))}
      </div>

      {/* CTA */}
      <div className="py-16 px-4" style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-4">Ready to Support Your Youth?</h2>
          <p className="text-white/60 mb-6">Log in and everything is already set up for you.</p>
          <Button onClick={() => navigate('/login')} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-5 text-lg">
            Sign In <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default StaffTutorialPage;
